import { type FC, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ShoppingBag,
  Search,
  Trash2,
  Plus,
  RefreshCw,
  Building2,
  MapPin,
  CheckCircle,
  XCircle,
  LayoutGrid,
  List,
  MoreHorizontal,
  AlertTriangle,
  Sparkles,
  Edit3,
} from 'lucide-react'
import { DialogHeaderWithIcon } from '../../components/ui/DialogHeaderWithIcon'
import { Card, CardContent } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/DropdownMenu'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../../components/ui/AlertDialog'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { AdminListingCard } from '../../components/admin/AdminListingCard'
import {
  adminApi,
  type MarketplaceProperty,
} from '../../services/api/admin'
import { useAuth } from '../../contexts/AuthContext'
import { cn } from '../../lib/utils'
import toast from 'react-hot-toast'

const VIEW_MODE_KEY = 'marketplace_admin_view_mode'

const PROPERTY_TYPES = [
  'commercial', 'residential', 'land', 'industrial',
  'multifamily', 'retail', 'office', 'hospitality', 'other',
]

const ASSET_CLASS_BADGE: Record<string, string> = {
  multifamily: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
  industrial: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300',
  office: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  retail: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
  healthcare: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  'self-storage': 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300',
}

const QUALIFIED_STATUS_BADGE: Record<string, string> = {
  qualified: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  pending_review: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  unqualified: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
}

const SkeletonCard: FC = () => (
  <Card className="flex flex-col overflow-hidden">
    <div className="h-40 bg-secondary animate-pulse" />
    <CardContent className="pt-4 space-y-3">
      <div className="h-4 bg-secondary animate-pulse rounded w-3/4" />
      <div className="h-3 bg-secondary animate-pulse rounded w-1/2" />
      <div className="grid grid-cols-2 gap-2">
        <div className="h-3 bg-secondary animate-pulse rounded" />
        <div className="h-3 bg-secondary animate-pulse rounded" />
      </div>
    </CardContent>
  </Card>
)

export const AdminMarketplacePage: FC = () => {
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [viewMode, setViewMode] = useState<'grid' | 'table'>(() => {
    return (localStorage.getItem(VIEW_MODE_KEY) as 'grid' | 'table') ?? 'grid'
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [seedConfirmOpen, setSeedConfirmOpen] = useState(false)
  const [seeding, setSeeding] = useState(false)

  useEffect(() => {
    if (!currentUser?.isAdmin) navigate('/app')
  }, [currentUser, navigate])

  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode)
  }, [viewMode])

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-marketplace-properties', typeFilter, statusFilter],
    queryFn: () =>
      adminApi.getMarketplaceProperties({
        ...(typeFilter ? { property_type: typeFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      }),
    enabled: !!currentUser?.isAdmin,
  })

  const allProperties = data?.properties ?? []
  const properties = searchQuery
    ? allProperties.filter((p) => {
        const query = searchQuery.toLowerCase()
        return (
          p.title.toLowerCase().includes(query) ||
          (p.sponsor ?? '').toLowerCase().includes(query) ||
          (p.addressCity ?? '').toLowerCase().includes(query) ||
          (p.addressState ?? '').toLowerCase().includes(query)
        )
      })
    : allProperties

  const handleEditProperty = (property: MarketplaceProperty) => {
    navigate(`/app/admin/marketplace/${property.id}/edit`)
  }

  const handleAddListing = () => {
    navigate('/app/admin/marketplace/new')
  }

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId) return
    try {
      setDeleting(true)
      await adminApi.deleteMarketplaceProperty(pendingDeleteId)
      toast.success('Listing deleted')
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-properties'] })
    } catch {
      toast.error('Failed to delete listing')
    } finally {
      setDeleting(false)
      setPendingDeleteId(null)
    }
  }

  const handleTogglePublished = async (property: MarketplaceProperty) => {
    try {
      await adminApi.toggleMarketplacePropertyActive(property.id, !property.published)
      toast.success(`Listing ${property.published ? 'unpublished' : 'published'}`)
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-properties'] })
    } catch {
      toast.error('Failed to update listing status')
    }
  }

  const handleSeedConfirm = async () => {
    try {
      setSeeding(true)
      const result = await adminApi.seedMarketplaceSamples()
      toast.success(`Seeded ${result.seeded_count} new listing${result.seeded_count !== 1 ? 's' : ''}`)
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace'] })
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-properties'] })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to seed sample listings'
      toast.error(message)
    } finally {
      setSeeding(false)
      setSeedConfirmOpen(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3 tracking-tight">
            <ShoppingBag className="h-8 w-8 text-primary" aria-hidden="true" />
            Marketplace
          </h1>
          <p className="text-muted-foreground mt-1">Manage DST offerings and listings</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setSeedConfirmOpen(true)}
          >
            <Sparkles className="h-4 w-4 mr-2" aria-hidden="true" />
            Seed Sample DSTs
          </Button>
          <Button onClick={handleAddListing}>
            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
            Add Listing
          </Button>
          <Button
            variant="outline"
            onClick={() => refetch()}
          >
            <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="text"
              placeholder="Search by title, sponsor, city…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              aria-label="Search listings"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              aria-label="Filter by property type"
              className={cn(
                'rounded-md border border-input bg-background px-3 py-2 text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              <option value="">All Types</option>
              {PROPERTY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
              className={cn(
                'rounded-md border border-input bg-background px-3 py-2 text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              <option value="">All Statuses</option>
              <option value="published">Published</option>
              <option value="unpublished">Draft</option>
            </select>
            <div className="flex rounded-md border border-input overflow-hidden">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="icon"
                title="Card grid view"
                className="rounded-none h-9 w-9"
                onClick={() => setViewMode('grid')}
              >
                <LayoutGrid className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="icon"
                title="Table view"
                className="rounded-none h-9 w-9"
                onClick={() => setViewMode('table')}
              >
                <List className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {isError && (
        <div className={cn(
          'flex items-center gap-3 rounded-lg border border-destructive/50',
          'bg-destructive/10 px-4 py-3 text-sm text-destructive'
        )}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>Failed to load listings.</span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}

      {isLoading && viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      )}

      {isLoading && viewMode === 'table' && (
        <Card className="p-8 flex items-center justify-center">
          <LoadingSpinner size="md" />
        </Card>
      )}

      {!isLoading && !isError && properties.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
          <div className="rounded-full bg-secondary p-4">
            <ShoppingBag className="h-8 w-8 opacity-50" aria-hidden="true" />
          </div>
          <p className="text-base font-medium">No listings yet — add your first DST offering</p>
          <Button onClick={handleAddListing}>
            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
            Add Listing
          </Button>
        </div>
      )}

      {!isLoading && !isError && properties.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {properties.map((property) => (
            <AdminListingCard
              key={property.id}
              property={property}
              onEdit={handleEditProperty}
              onDelete={setPendingDeleteId}
              onTogglePublished={handleTogglePublished}
            />
          ))}
        </div>
      )}

      {!isLoading && !isError && properties.length > 0 && viewMode === 'table' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Property
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Asset Class
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Qualified
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {properties.map((property) => (
                  <tr key={property.id} className="hover:bg-secondary/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {property.images?.[0] ? (
                            <img
                              src={property.images[0]}
                              alt={property.title}
                              className="h-10 w-10 rounded-lg object-cover"
                              onError={(e) => {
                                const target = e.currentTarget
                                target.style.display = 'none'
                                const parent = target.parentElement
                                if (parent) {
                                  const fallback = parent.querySelector('[data-fallback]')
                                  if (fallback) (fallback as HTMLElement).style.display = 'flex'
                                }
                              }}
                            />
                          ) : null}
                          <Building2
                            className="h-5 w-5 text-muted-foreground"
                            style={{ display: property.images?.[0] ? 'none' : 'block' }}
                            data-fallback="true"
                            aria-hidden="true"
                          />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{property.title}</p>
                          {property.sponsor && (
                            <p className="text-xs text-muted-foreground">{property.sponsor}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {property.dstAssetClass ? (
                        <span className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                          ASSET_CLASS_BADGE[property.dstAssetClass] ?? 'bg-secondary text-foreground'
                        )}>
                          {property.dstAssetClass}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                        {[property.addressCity, property.addressState].filter(Boolean).join(', ') || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={cn(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                        property.published
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                          : 'bg-secondary text-foreground'
                      )}>
                        {property.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={cn(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                        QUALIFIED_STATUS_BADGE[property.qualifiedStatus] ?? 'bg-secondary text-foreground'
                      )}>
                        {property.qualifiedStatus.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" title="Listing actions">
                            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditProperty(property)}>
                            <Edit3 className="h-4 w-4 mr-2" aria-hidden="true" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleTogglePublished(property)}>
                            {property.published ? (
                              <><XCircle className="h-4 w-4 mr-2" aria-hidden="true" />Unpublish</>
                            ) : (
                              <><CheckCircle className="h-4 w-4 mr-2" aria-hidden="true" />Publish</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setPendingDeleteId(property.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AlertDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => { if (!open) setPendingDeleteId(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <DialogHeaderWithIcon
              icon={Trash2}
              iconColor="rose"
              title="Delete listing?"
              description="This listing will be permanently removed. This action cannot be undone."
              variant="alertDialog"
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              onClick={() => setPendingDeleteId(null)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={seedConfirmOpen}
        onOpenChange={(open) => { if (!open) setSeedConfirmOpen(false) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <DialogHeaderWithIcon
              icon={Sparkles}
              iconColor="amber"
              title="Seed sample DST listings?"
              description="Seed 8 sample DST listings. This is idempotent — re-running won't duplicate."
              variant="alertDialog"
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={seeding}
              onClick={() => setSeedConfirmOpen(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={seeding}
              onClick={handleSeedConfirm}
            >
              {seeding ? 'Seeding…' : 'Seed listings'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
